import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://jwkmhgishqslmdlbhqqz.supabase.co';

const severityColors = {
  critical: '#d32f2f',
  warning: '#f57c00',
  info: '#1976d2',
};

const statusColors = {
  compliant: '#388e3c',
  needs_attention: '#f57c00',
  non_compliant: '#d32f2f',
};

const priorityColors = {
  high: '#d32f2f',
  medium: '#f57c00',
  low: '#1976d2',
};

function AdminInsightsPanel({ userInfo }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    flags: true,
    unitStatus: false,
    trends: false,
    recommendations: false,
  });

  useEffect(() => {
    fetchLatestInsight();
  }, []);

  const fetchLatestInsight = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_insights')
        .select('*')
        .eq('deployment_id', 'mayo')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching insight:', error);
        return;
      }
      setInsight(data && data.length > 0 ? data[0] : null);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      await fetch(`${SUPABASE_URL}/functions/v1/generate-daily-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ deployment_id: 'mayo' }),
      });

      await fetchLatestInsight();
    } catch (err) {
      console.error('Error regenerating:', err);
    } finally {
      setRegenerating(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    if (!insight) return;
    try {
      await supabase
        .from('admin_insights')
        .update({
          status,
          reviewed_by: userInfo?.id || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', insight.id);

      setInsight((prev) => ({ ...prev, status, reviewed_at: new Date().toISOString() }));
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const { data, error } = await supabase
        .from('admin_insights')
        .select('id, created_at, summary, status, flags')
        .eq('deployment_id', 'mayo')
        .order('created_at', { ascending: false })
        .limit(14);

      if (error) {
        console.error('Error fetching history:', error);
        return;
      }
      setHistory(data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleShowHistory = () => {
    setShowHistory(true);
    fetchHistory();
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>AI Compliance Insights</h2>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          Loading insights...
        </div>
      </div>
    );
  }

  if (!insight) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>AI Compliance Insights</h2>
        </div>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            No insights generated yet. Click Regenerate to create the first report.
          </p>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            style={styles.btnPrimary}
          >
            {regenerating ? 'Generating...' : 'Regenerate Daily'}
          </button>
        </div>
      </div>
    );
  }

  const flags = insight.flags || [];
  const criticalCount = flags.filter((f) => f.severity === 'critical').length;
  const warningCount = flags.filter((f) => f.severity === 'warning').length;
  const infoCount = flags.filter((f) => f.severity === 'info').length;
  const trends = insight.trends || [];
  const recommendations = insight.recommendations || [];
  const unitStatus = insight.unit_status || {};

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.headerTitle}>AI Compliance Insights</h2>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            style={styles.btnPrimary}
          >
            {regenerating ? 'Generating...' : 'Regenerate Daily'}
          </button>
          <button
            onClick={() => handleStatusUpdate('reviewed')}
            style={{
              ...styles.btnSecondary,
              opacity: insight.status === 'reviewed' ? 0.5 : 1,
            }}
            disabled={insight.status === 'reviewed'}
          >
            Mark Reviewed
          </button>
          <button
            onClick={() => handleStatusUpdate('dismissed')}
            style={styles.btnOutline}
          >
            Dismiss
          </button>
          <button onClick={handleShowHistory} style={styles.btnOutline}>
            View History
          </button>
        </div>
      </div>

      {/* Severity Badges */}
      <div style={styles.badgeRow}>
        <span style={{ ...styles.badge, background: severityColors.critical }}>
          {criticalCount} Critical
        </span>
        <span style={{ ...styles.badge, background: severityColors.warning }}>
          {warningCount} Warning
        </span>
        <span style={{ ...styles.badge, background: severityColors.info }}>
          {infoCount} Info
        </span>
        {insight.status && (
          <span
            style={{
              ...styles.badge,
              background: insight.status === 'reviewed' ? '#388e3c' : insight.status === 'dismissed' ? '#666' : '#1976d2',
              marginLeft: 'auto',
            }}
          >
            {insight.status}
          </span>
        )}
      </div>

      {/* Executive Summary */}
      <div style={styles.summaryBox}>
        <h3 style={styles.sectionTitle}>Executive Summary</h3>
        <p style={{ color: '#444', lineHeight: '1.6', margin: 0 }}>
          {insight.summary || 'No summary available.'}
        </p>
      </div>

      {/* Flags Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader} onClick={() => toggleSection('flags')}>
          <h3 style={styles.sectionTitle}>
            Flags ({flags.length})
          </h3>
          <span style={styles.chevron}>{expandedSections.flags ? '\u25B2' : '\u25BC'}</span>
        </div>
        {expandedSections.flags && (
          <div style={{ padding: '0 16px 16px' }}>
            {flags.length === 0 ? (
              <p style={{ color: '#666', fontStyle: 'italic' }}>No flags raised.</p>
            ) : (
              flags.map((flag, i) => (
                <div key={i} style={{ ...styles.flagCard, borderLeftColor: severityColors[flag.severity] || '#999' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span
                      style={{
                        ...styles.badgeSmall,
                        background: severityColors[flag.severity] || '#999',
                      }}
                    >
                      {flag.severity}
                    </span>
                    {flag.unit && <span style={{ color: '#666', fontSize: '13px' }}>{flag.unit}</span>}
                  </div>
                  <strong style={{ color: '#333' }}>{flag.title}</strong>
                  <p style={{ color: '#555', fontSize: '14px', margin: '4px 0 0' }}>{flag.detail}</p>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Unit Status Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader} onClick={() => toggleSection('unitStatus')}>
          <h3 style={styles.sectionTitle}>Unit Status</h3>
          <span style={styles.chevron}>{expandedSections.unitStatus ? '\u25B2' : '\u25BC'}</span>
        </div>
        {expandedSections.unitStatus && (
          <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Unit</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Logs</th>
                  <th style={styles.th}>Last Log</th>
                  <th style={styles.th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(unitStatus).map(([name, info]) => (
                  <tr key={name}>
                    <td style={styles.td}>{name}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badgeSmall,
                          background: statusColors[info.status] || '#999',
                        }}
                      >
                        {(info.status || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={styles.td}>{info.log_count ?? 0}</td>
                    <td style={styles.td}>
                      {info.last_log ? new Date(info.last_log).toLocaleString() : 'N/A'}
                    </td>
                    <td style={styles.td}>{info.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Trends Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader} onClick={() => toggleSection('trends')}>
          <h3 style={styles.sectionTitle}>Trends ({trends.length})</h3>
          <span style={styles.chevron}>{expandedSections.trends ? '\u25B2' : '\u25BC'}</span>
        </div>
        {expandedSections.trends && (
          <div style={{ padding: '0 16px 16px' }}>
            {trends.length === 0 ? (
              <p style={{ color: '#666', fontStyle: 'italic' }}>No trends identified.</p>
            ) : (
              trends.map((trend, i) => {
                const icon = trend.type === 'positive' ? '\u25B2' : trend.type === 'negative' ? '\u25BC' : '\u2014';
                const color = trend.type === 'positive' ? '#388e3c' : trend.type === 'negative' ? '#d32f2f' : '#666';
                return (
                  <div key={i} style={styles.trendItem}>
                    <span style={{ color, fontWeight: 'bold', marginRight: '8px', fontSize: '16px' }}>{icon}</span>
                    <div>
                      <strong style={{ color: '#333' }}>{trend.title}</strong>
                      <p style={{ color: '#555', fontSize: '14px', margin: '2px 0 0' }}>{trend.detail}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Recommendations Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader} onClick={() => toggleSection('recommendations')}>
          <h3 style={styles.sectionTitle}>
            Recommendations ({recommendations.length})
          </h3>
          <span style={styles.chevron}>{expandedSections.recommendations ? '\u25B2' : '\u25BC'}</span>
        </div>
        {expandedSections.recommendations && (
          <div style={{ padding: '0 16px 16px' }}>
            {recommendations.length === 0 ? (
              <p style={{ color: '#666', fontStyle: 'italic' }}>No recommendations.</p>
            ) : (
              recommendations.map((rec, i) => (
                <div key={i} style={styles.recItem}>
                  <span
                    style={{
                      ...styles.badgeSmall,
                      background: priorityColors[rec.priority] || '#999',
                      marginRight: '8px',
                    }}
                  >
                    {rec.priority}
                  </span>
                  <div>
                    <strong style={{ color: '#333' }}>{rec.title}</strong>
                    <p style={{ color: '#555', fontSize: '14px', margin: '2px 0 0' }}>{rec.detail}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <span>Model: {insight.model_used || 'N/A'}</span>
        <span>Tokens: {insight.tokens_used || 0}</span>
        <span>Generated: {new Date(insight.created_at).toLocaleString()}</span>
      </div>

      {/* History Modal */}
      {showHistory && (
        <div style={styles.modalOverlay} onClick={() => setShowHistory(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#333' }}>Insight History (Last 14 Days)</h3>
              <button onClick={() => setShowHistory(false)} style={styles.closeBtn}>&times;</button>
            </div>
            {historyLoading ? (
              <p style={{ color: '#666', textAlign: 'center' }}>Loading...</p>
            ) : history.length === 0 ? (
              <p style={{ color: '#666', textAlign: 'center' }}>No history found.</p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {history.map((h) => {
                  const hFlags = h.flags || [];
                  const hCritical = hFlags.filter((f) => f.severity === 'critical').length;
                  const hWarning = hFlags.filter((f) => f.severity === 'warning').length;
                  return (
                    <div key={h.id} style={styles.historyItem}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600', color: '#333' }}>
                          {new Date(h.created_at).toLocaleDateString()}
                        </span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {hCritical > 0 && (
                            <span style={{ ...styles.badgeTiny, background: '#d32f2f' }}>{hCritical}C</span>
                          )}
                          {hWarning > 0 && (
                            <span style={{ ...styles.badgeTiny, background: '#f57c00' }}>{hWarning}W</span>
                          )}
                          <span
                            style={{
                              ...styles.badgeTiny,
                              background: h.status === 'reviewed' ? '#388e3c' : h.status === 'dismissed' ? '#666' : '#1976d2',
                            }}
                          >
                            {h.status}
                          </span>
                        </div>
                      </div>
                      <p style={{ color: '#555', fontSize: '13px', margin: '4px 0 0', lineHeight: '1.4' }}>
                        {(h.summary || '').slice(0, 150)}{h.summary && h.summary.length > 150 ? '...' : ''}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    marginBottom: '24px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '20px',
    borderBottom: '1px solid #eee',
  },
  headerTitle: {
    margin: 0,
    fontSize: '20px',
    color: '#333',
    fontWeight: '600',
  },
  badgeRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    padding: '16px 20px',
    alignItems: 'center',
  },
  badge: {
    color: '#fff',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
  },
  badgeSmall: {
    color: '#fff',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    textTransform: 'uppercase',
    fontWeight: '600',
    display: 'inline-block',
  },
  badgeTiny: {
    color: '#fff',
    padding: '1px 6px',
    borderRadius: '3px',
    fontSize: '10px',
    fontWeight: '600',
  },
  summaryBox: {
    padding: '16px 20px',
    background: '#f8f9fa',
    margin: '0 16px 16px',
    borderRadius: '8px',
  },
  section: {
    border: '1px solid #eee',
    borderRadius: '8px',
    margin: '0 16px 12px',
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    cursor: 'pointer',
    background: '#fafafa',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '15px',
    color: '#333',
    fontWeight: '600',
  },
  chevron: {
    fontSize: '12px',
    color: '#999',
  },
  flagCard: {
    padding: '12px',
    marginBottom: '8px',
    borderLeft: '4px solid #999',
    background: '#fafafa',
    borderRadius: '0 8px 8px 0',
  },
  trendItem: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  recItem: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    padding: '8px',
    textAlign: 'left',
    borderBottom: '2px solid #eee',
    color: '#666',
    fontWeight: '600',
    fontSize: '13px',
  },
  td: {
    padding: '8px',
    borderBottom: '1px solid #f0f0f0',
    color: '#333',
  },
  footer: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    padding: '12px 20px',
    borderTop: '1px solid #eee',
    fontSize: '12px',
    color: '#888',
  },
  btnPrimary: {
    padding: '8px 16px',
    background: '#0E3293',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
  },
  btnSecondary: {
    padding: '8px 16px',
    background: '#388e3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
  },
  btnOutline: {
    padding: '8px 16px',
    background: '#fff',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflow: 'hidden',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
    padding: '0 4px',
  },
  historyItem: {
    padding: '12px',
    borderBottom: '1px solid #f0f0f0',
  },
};

export default AdminInsightsPanel;
